import { gql } from '@apollo/client/core'
import { watch } from 'vue'

import { useApollo, useGuild, useSession } from '@/shared/ports'
import type {
  GuildNotificationCapabilitiesWire,
  GuildNotificationSettingsWire,
  GuildNotificationTimerListWire,
  GuildNotificationTimerStatusChannelWire,
} from '../lib/handWrittenGraphql'
import { isForbiddenLiveSyncError } from './liveAccessRevocation'
import type { NotificationSettingsApi } from './notificationSettings'
import type { NotificationTimersApi } from './notificationTimers'

export const GuildNotificationStateChangedDocument = gql`
  subscription GuildNotificationStateChanged($guildId: Int!) {
    guildNotificationStateChanged(guildId: $guildId) {
      type
      state {
        capabilities {
          guildId
          coordinatorPresent
          bothBotsPresent
          configuredBotAccounts
          availableBotAccounts
          maxConcurrentChannels
          teamspeakConfigured
          botAccountStatuses {
            index
            role
            ready
            guildPresent
            inviteUrl
            requiresOauthCodeGrant
          }
          supportedModes
        }
        settings {
          guildId
          enabled
          automatic
          intervalSeconds
          notificationOffsetsSeconds
          mode
          voiceChannelIds
          teamspeakChannelIds
          soundboardSoundId
          soundboardSourceGuildId
        }
        timers {
          guildId
          timers {
            id
            name
            source
            startsAt
            endsAt
            durationSeconds
            active
            cancelledAt
            status
            canModify
          }
        }
        timerStatusChannel {
          guildId
          channelId
          messageId
        }
      }
    }
  }
`

export interface GuildNotificationStateChangedSubscription {
  readonly guildNotificationStateChanged: {
    readonly type: string
    readonly state: {
      readonly capabilities: GuildNotificationCapabilitiesWire
      readonly settings: GuildNotificationSettingsWire
      readonly timers: GuildNotificationTimerListWire | null
      readonly timerStatusChannel: GuildNotificationTimerStatusChannelWire | null
    } | null
  }
}

export function useNotificationLiveSync(
  settingsApi: NotificationSettingsApi,
  timersApi: NotificationTimersApi,
): void {
  const apollo = useApollo()
  const guild = useGuild()
  const session = useSession()

  watch(
    [guild.dbGuildId, guild.activeDiscordGuildId, session.user],
    ([dbGuildId, discordGuildId], _, onCleanup) => {
      settingsApi.prepareGuild(discordGuildId)
      timersApi.prepareGuild(discordGuildId)
      if (
        dbGuildId === null
        || discordGuildId === '0'
      ) {
        timersApi.markUnavailable(discordGuildId)
        return
      }
      if (!session.isOfficerOf(discordGuildId)) {
        settingsApi.prepareGuild('0')
        timersApi.prepareGuild('0')
        return
      }
      const numericGuildId = Number(dbGuildId)
      if (!Number.isSafeInteger(numericGuildId) || numericGuildId <= 0) {
        timersApi.markUnavailable(discordGuildId)
        return
      }

      const subscription = apollo.subscribe<GuildNotificationStateChangedSubscription>({
        query: GuildNotificationStateChangedDocument,
        variables: { guildId: numericGuildId },
      }).subscribe({
        next: (event) => {
          if (isForbiddenLiveSyncError(event.errors)) {
            settingsApi.prepareGuild('0')
            timersApi.prepareGuild('0')
            settingsApi.reportLiveError('FORBIDDEN: notification settings access was revoked.')
            timersApi.reportLiveError('FORBIDDEN: notification timer access was revoked.')
            return
          }
          const state = event.data?.guildNotificationStateChanged.state
          if (state === null || state === undefined) {
            settingsApi.reportLiveError(
              'A live notification update arrived without state. Waiting for the next update.',
            )
            timersApi.reportLiveError(
              'A live notification timer update arrived without state. Waiting for the next update.',
            )
            return
          }
          if (
            state.capabilities.guildId !== discordGuildId
            || state.settings.guildId !== discordGuildId
          ) {
            settingsApi.reportLiveError('A live notification update targeted a different guild.')
            timersApi.reportLiveError('A live notification update targeted a different guild.')
            return
          }
          settingsApi.applyLiveState(state.capabilities, state.settings)
          if (!state.capabilities.coordinatorPresent && !state.capabilities.teamspeakConfigured) {
            timersApi.markUnavailable(discordGuildId)
            return
          }
          if (state.timers === null || state.timerStatusChannel === null) {
            timersApi.reportLiveError(
              'Live notification timer state is temporarily unavailable. Waiting for the next update.',
            )
            return
          }
          if (
            state.timers.guildId !== discordGuildId
            || state.timerStatusChannel.guildId !== discordGuildId
          ) {
            timersApi.reportLiveError('A live notification timer update targeted a different guild.')
            return
          }
          timersApi.applyLiveState(state.timers, state.timerStatusChannel)
        },
        error: (cause: unknown) => {
          const message = cause instanceof Error ? cause.message : String(cause)
          if (isForbiddenLiveSyncError(cause)) {
            settingsApi.prepareGuild('0')
            timersApi.prepareGuild('0')
          }
          settingsApi.reportLiveError(message)
          timersApi.reportLiveError(message)
        },
      })
      onCleanup(() => subscription.unsubscribe())
    },
    { immediate: true },
  )
}
